import React, {
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
} from 'react';
import { ulid } from 'ulid';
import { DateTime } from 'luxon';
import {
	PdfViewer,
	PdfViewerHandle,
	PdfViewerOverlayContext,
	Size,
} from '@/components/pdf-viewer';
import { DocumentAnnotation } from '../annotation/document-annotation';
import { Annotation, DEFAULT_DIMENSIONS } from '../annotation/annotation';
import { DocumentSignerOption } from '../../document-annotator/utils';
import { usePdfTextMetrics } from '../../../../hooks/use-pdf-text-metrics';
import notifyHB from '@/config/notifyHB';
import { getSafeAnnotationXY } from '@/utils/get-safe-annotation-xy';
import { getClientDeviceType } from '@/utils/get-client-device-type';

export type PdfPageHandle = {
	/**
	 * Places a new field of `type` centred on a viewport point (e.g. where a palette
	 * drag was released). Returns false when the point is not over a page.
	 */
	dropAnnotationAt: (
		type: Annotation['type'],
		clientX: number,
		clientY: number,
	) => boolean;
};

type PdfPageProps = {
	file: File;
	currentEmail: string;
	userName: string;
	annotations: Annotation[];
	documentSigners: DocumentSignerOption[];
	handleRef?: React.Ref<PdfPageHandle>;
	thumbnailsTourId?: string;
	onDropNote: (annotation: Annotation) => void;
	onRemoveNote: (annotationId: Annotation['id']) => void;
	onUpdate: (id: Annotation['id'], updatedAnnotation: Annotation) => void;
};

/**
 * Annotator canvas: the whole document in a PdfViewer with every page's fields on
 * its overlay. Field geometry is stored in base px of the page (96 dpi) with
 * containerRect = the page's base size, so it is independent of zoom and viewport.
 */
export const PdfPage = (props: PdfPageProps) => {
	const documentSignersById = useMemo<Record<string, DocumentSignerOption>>(
		() =>
			props.documentSigners.reduce(
				(mapped, signer) => ({ ...mapped, [signer.value]: signer }),
				{},
			),
		[props.documentSigners],
	);
	const viewerRef = useRef<PdfViewerHandle>(null);
	const metrics = usePdfTextMetrics();

	const { currentEmail, userName, onDropNote } = props;

	const createAnnotation = useCallback(
		(
			type: Annotation['type'],
			pageIndex: number,
			pageSize: Size,
			center: { x: number; y: number },
		) => {
			const date = DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd');
			const userInitials = userName
				.split(' ')
				.map((name) => name[0])
				.join('');
			const value =
				type === 'date'
					? date
					: type === 'text'
						? ''
						: type === 'full_name'
							? userName
							: type === 'initials'
								? userInitials
								: undefined;
			if (value && !metrics) {
				notifyHB('sign-document', 'PdfPage', 'text metrics not loaded');
				return;
			}
			const width =
				value && metrics
					? metrics.measure(value).widthPx
					: DEFAULT_DIMENSIONS.WIDTH;
			const height = DEFAULT_DIMENSIONS.HEIGHT;
			onDropNote({
				id: ulid(),
				page: pageIndex,
				...getSafeAnnotationXY(
					center.x - width / 2,
					center.y - height / 2,
					pageSize,
					{ width, height },
				),
				width,
				height,
				containerRect: {
					width: pageSize.width,
					height: pageSize.height,
				},
				type,
				email: currentEmail,
				value,
			});
		},
		[currentEmail, userName, metrics, onDropNote],
	);

	const dropAnnotationAt = useCallback(
		(type: Annotation['type'], clientX: number, clientY: number) => {
			const viewer = viewerRef.current;
			if (!currentEmail || !viewer) return false;
			const point = viewer.clientPointToPage(clientX, clientY);
			if (!point) return false;
			const pageSize = viewer.getPageSizes()[point.pageIndex];
			createAnnotation(type, point.pageIndex, pageSize, point);
			return true;
		},
		[currentEmail, createAnnotation],
	);

	useImperativeHandle(props.handleRef, () => ({ dropAnnotationAt }), [
		dropAnnotationAt,
	]);

	const isDesktop = getClientDeviceType() === 'desktop';
	const { annotations, onUpdate, onRemoveNote } = props;

	const renderPageOverlay = useCallback(
		({ pageIndex, size, zoom }: PdfViewerOverlayContext) => (
			<>
				{!isDesktop && (
					// Touch devices have no palette: tapping the page places a signature.
					<div
						className="absolute inset-0"
						onClick={(event) =>
							dropAnnotationAt(
								'manuscript',
								event.clientX,
								event.clientY,
							)
						}
					/>
				)}
				{annotations
					.filter((annotation) => annotation.page === pageIndex)
					.map((annotation) => (
						<DocumentAnnotation
							key={annotation.id}
							color={documentSignersById[annotation.email]?.color}
							annotation={annotation}
							pageSize={size}
							zoom={zoom}
							onUpdate={onUpdate}
							onRemove={onRemoveNote}
							isResizable={['manuscript', 'text'].includes(
								annotation.type,
							)}
						/>
					))}
			</>
		),
		[
			isDesktop,
			annotations,
			documentSignersById,
			onUpdate,
			onRemoveNote,
			dropAnnotationAt,
		],
	);

	const handleError = useCallback(
		(error: unknown) =>
			notifyHB('sign-document', 'PdfPage>PdfViewer', error),
		[],
	);

	return (
		<PdfViewer
			file={props.file}
			handleRef={viewerRef}
			renderPageOverlay={renderPageOverlay}
			onError={handleError}
			thumbnailsTourId={props.thumbnailsTourId}
		/>
	);
};
